// Messenger — 1:1 DMs with text / emoji / image / RPS mini-game / video call
import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Send, Image as ImageIcon, Smile, Video, Gamepad2, Phone, Search } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast, Toaster } from "sonner";
import LiveKitStreamRoom from "../components/LiveKitStreamRoom";

const EMOJIS = ["😀","😂","😍","🥳","🔥","💯","🎮","👑","💎","✨","🚀","🎉","❤️","👏","🙌","🤝","🤯","💀","😈","🎯"];

export default function Messenger() {
  const { user } = useAuth();
  const nav = useNavigate();
  const { convId } = useParams();
  const [convs, setConvs] = useState([]);
  const [activeId, setActiveId] = useState(convId || null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [callRoom, setCallRoom] = useState(null);
  const [rps, setRps] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const fileRef = useRef(null);
  const scrollRef = useRef(null);

  const loadConvs = () => api.get("/dm/conversations").then(({ data }) => setConvs(data)).catch(() => {});
  useEffect(() => {
    if (!user) return;
    loadConvs();
    const t = setInterval(loadConvs, 6000);
    return () => clearInterval(t);
  }, [user]);

  useEffect(() => { if (convId) setActiveId(convId); }, [convId]);

  useEffect(() => {
    if (!activeId) return;
    const fetchMsgs = () => api.get(`/dm/${activeId}/messages`).then(({ data }) => setMessages(data)).catch(() => {});
    const fetchRps = () => api.get(`/games/rps/state/${activeId}`).then(({ data }) => setRps(data)).catch(() => {});
    fetchMsgs(); fetchRps();
    const t = setInterval(() => { fetchMsgs(); fetchRps(); }, 4000);
    return () => clearInterval(t);
  }, [activeId]);

  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }); }, [messages]);

  const send = async ({ text, image_b64, video_call_room, game_invite } = {}) => {
    const t = text ?? draft;
    if (!t && !image_b64 && !video_call_room && !game_invite) return;
    try {
      await api.post("/dm/send", { conv_id: activeId, text: t || null, image_b64: image_b64 || null,
                                    video_call_room: video_call_room || null, game_invite: game_invite || null });
      setDraft(""); setEmojiOpen(false);
      api.get(`/dm/${activeId}/messages`).then(({ data }) => setMessages(data));
      loadConvs();
    } catch { toast.error("Send failed"); }
  };

  const pickImage = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 3_000_000) { toast.error("Max 3MB"); return; }
    const r = new FileReader();
    r.onload = () => send({ image_b64: r.result });
    r.readAsDataURL(f);
  };

  const startCall = async () => {
    try {
      const { data } = await api.post(`/dm/${activeId}/call/start`);
      setCallRoom(data.room);
      toast.success("Call started");
    } catch { toast.error("Call failed"); }
  };

  const playRps = async (choice) => {
    try {
      const { data } = await api.post("/games/rps/play", { conv_id: activeId, choice });
      setRps(data);
      send({ game_invite: "rps" });
    } catch { toast.error("RPS failed"); }
  };

  const startNewDm = async (otherId) => {
    try {
      const { data } = await api.post(`/dm/open/${otherId}`);
      setActiveId(data.id); setSearchOpen(false);
      loadConvs(); nav(`/messages/${data.id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Open failed"); }
  };

  const searchUsers = async (q) => {
    if (!q.trim()) { setSearchResults([]); return; }
    try {
      const { data } = await api.get("/users", { params: { q, limit: 10 } });
      setSearchResults(data.filter((u) => u.id !== user.id));
    } catch {}
  };

  if (!user) return <div className="p-20 text-center text-white/40">Sign in to use Messenger.</div>;
  const active = convs.find((c) => c.id === activeId);

  return (
    <div className="space-y-3" data-testid="messenger-page">
      <Toaster theme="dark" position="top-center" />
      <h1 className="font-display font-black text-3xl tracking-tighter">Messages.</h1>

      <div className="grid lg:grid-cols-[300px_1fr] gap-px bg-white/5 min-h-[70vh]">
        {/* CONVERSATION LIST */}
        <aside className="bg-nexus-surface p-3 space-y-2">
          <button onClick={() => setSearchOpen(true)} className="btn-ghost w-full justify-center text-[10px]" data-testid="new-dm-btn"><Search className="w-3 h-3" />New message</button>
          {convs.length === 0 && <div className="text-white/40 text-xs p-3">No conversations yet.</div>}
          {convs.map((c) => (
            <button key={c.id} onClick={() => { setActiveId(c.id); nav(`/messages/${c.id}`); }} data-testid={`conv-${c.id}`}
              className={`w-full text-left p-3 border ${activeId === c.id ? "border-nexus-cyan bg-nexus-panel" : "border-white/5 hover:border-white/20"} flex items-center gap-3`}>
              <img src={c.other?.avatar} alt="" className="w-9 h-9 ring-1 ring-white/10" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{c.other?.display_name || "—"}</div>
                <div className="text-[10px] text-white/40 truncate">{c.last_message || "Tap to start"}</div>
              </div>
            </button>
          ))}
        </aside>

        {/* CHAT */}
        <section className="bg-nexus-surface flex flex-col">
          {!active && <div className="flex-1 flex items-center justify-center text-white/40 text-sm">Select a conversation</div>}
          {active && (
            <>
              <div className="p-3 border-b border-white/10 flex items-center gap-3">
                <img src={active.other?.avatar} alt="" className="w-9 h-9 ring-1 ring-white/20" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium">{active.other?.display_name}</div>
                  <div className="text-[10px] text-white/40">Direct message</div>
                </div>
                <button onClick={startCall} className="btn-ghost text-[10px]" data-testid="dm-video-call"><Video className="w-3 h-3" />Video call</button>
              </div>

              {/* VIDEO CALL OVERLAY */}
              {callRoom && (
                <div className="aspect-video bg-black relative">
                  <LiveKitStreamRoom streamId={`call-${callRoom}`} role="cohost" />
                  <button onClick={() => setCallRoom(null)} className="absolute top-2 right-2 bg-nexus-crimson px-3 py-1 text-[10px] tracking-[0.3em] uppercase font-display">END CALL</button>
                </div>
              )}

              {/* RPS GAME PANEL */}
              {rps && rps.status !== "resolved" && (
                <div className="p-3 bg-nexus-panel/30 border-b border-white/10 text-xs text-white/70 flex items-center gap-2" data-testid="rps-active">
                  <Gamepad2 className="w-3.5 h-3.5 text-nexus-amber" />
                  <span>Rock-Paper-Scissors round in progress. Pick:</span>
                  <button onClick={() => playRps("rock")} className="px-2 py-1 border border-white/10 hover:border-nexus-amber" data-testid="rps-rock">🪨 Rock</button>
                  <button onClick={() => playRps("paper")} className="px-2 py-1 border border-white/10 hover:border-nexus-amber" data-testid="rps-paper">📄 Paper</button>
                  <button onClick={() => playRps("scissors")} className="px-2 py-1 border border-white/10 hover:border-nexus-amber" data-testid="rps-scissors">✂️ Scissors</button>
                </div>
              )}
              {rps && rps.status === "resolved" && (
                <div className="p-3 bg-nexus-panel/30 border-b border-white/10 text-xs flex items-center gap-3">
                  <span className="text-nexus-amber">RPS result:</span>
                  <span className="text-white/70">{rps.winner ? (rps.winner === user.id ? "🏆 You won!" : "💀 You lost") : "🤝 Draw"}</span>
                </div>
              )}

              {/* MESSAGES */}
              <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-2 min-h-[40vh] max-h-[55vh]">
                {messages.length === 0 && <div className="text-white/30 text-sm text-center py-10">Say hi 👋</div>}
                {messages.map((m) => {
                  const mine = m.from_id === user.id;
                  return (
                    <div key={m.id} className={`flex gap-2 ${mine ? "flex-row-reverse" : ""}`} data-testid={`msg-${m.id}`}>
                      <img src={m.from_avatar} alt="" className="w-7 h-7 ring-1 ring-white/10" />
                      <div className={`max-w-[70%] p-2.5 text-sm ${mine ? "bg-nexus-cyan/15 border-r-2 border-nexus-cyan" : "bg-nexus-panel border-l-2 border-white/10"}`}>
                        {m.text && <div className="whitespace-pre-wrap">{m.text}</div>}
                        {m.media_url && <img src={m.media_url} alt="" className="mt-1 max-h-64" />}
                        {m.kind === "video_call" && <button onClick={() => setCallRoom(m.video_call_room)} className="text-nexus-magenta underline">Join video call →</button>}
                        {m.kind === "game" && <span className="text-nexus-amber">🎮 {m.game_invite?.toUpperCase()} invite</span>}
                        <div className="text-[9px] text-white/30 mt-1 tracking-wider">{new Date(m.ts).toLocaleTimeString()}</div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* COMPOSER */}
              <div className="p-3 border-t border-white/10 space-y-2 relative">
                {emojiOpen && (
                  <div className="absolute bottom-full left-3 right-3 mb-2 bg-nexus-panel border border-white/10 p-2 grid grid-cols-10 gap-1" data-testid="emoji-picker">
                    {EMOJIS.map((e) => <button key={e} onClick={() => setDraft((d) => d + e)} className="text-xl hover:bg-white/10 p-1">{e}</button>)}
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <button onClick={() => setEmojiOpen(!emojiOpen)} className="text-white/60 hover:text-nexus-cyan" data-testid="emoji-toggle"><Smile className="w-4 h-4" /></button>
                  <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={pickImage} />
                  <button onClick={() => fileRef.current?.click()} className="text-white/60 hover:text-nexus-cyan" data-testid="image-btn"><ImageIcon className="w-4 h-4" /></button>
                  <button onClick={() => playRps("rock")} className="text-white/60 hover:text-nexus-amber" data-testid="game-btn" title="Start Rock-Paper-Scissors"><Gamepad2 className="w-4 h-4" /></button>
                  <input value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Message…" className="flex-1 bg-transparent border border-white/10 px-3 py-2 text-sm" data-testid="dm-input" />
                  <button onClick={() => send()} className="btn-nexus" data-testid="dm-send"><Send className="w-3 h-3" /></button>
                </div>
              </div>
            </>
          )}
        </section>
      </div>

      {searchOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-6" onClick={() => setSearchOpen(false)}>
          <div className="glass-strong p-5 max-w-md w-full space-y-3" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display text-lg">Start a new message</h3>
            <input autoFocus onChange={(e) => searchUsers(e.target.value)} placeholder="Search by name…" className="w-full bg-transparent border border-white/10 px-3 py-2 text-sm" data-testid="search-users-input" />
            <div className="space-y-1 max-h-72 overflow-y-auto">
              {searchResults.map((u) => (
                <button key={u.id} onClick={() => startNewDm(u.id)} className="w-full text-left flex items-center gap-3 p-2 hover:bg-white/5" data-testid={`search-result-${u.id}`}>
                  <img src={u.avatar} alt="" className="w-8 h-8 ring-1 ring-white/10" />
                  <span className="text-sm">{u.display_name}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
